import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export const TOOL_COLOR_META = {
  rosu: { label: "Roșu", plural: "roșii", hex: "#EF4444" },
  galben: { label: "Galben", plural: "galbene", hex: "#FDE047" },
  albastru: { label: "Albastru", plural: "albastre", hex: "#3B82F6" },
  verde: { label: "Verde", plural: "verzi", hex: "#10B981" },
  gri: { label: "Gri", plural: "gri", hex: "#6B7280" },
  mov: { label: "Mov", plural: "mov", hex: "#A855F7" },
};

export const ToolColorDots = ({ projectId, regieId, colors = [], onChanged }) => {
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/tool-sets/site-colors", {
        project_id: projectId || null,
        regie_id: regieId || null,
        colors: next,
      });
      toast.success(next.length ? `Scule ${next.map((c) => TOOL_COLOR_META[c].label).join(" + ")} · ${data.updated_entries} alocări actualizate` : `Culori scoase (${data.updated_entries} alocări)`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvarea culorilor");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (code) => {
    let next;
    if (colors.includes(code)) next = colors.filter((c) => c !== code);
    else if (colors.length >= 2) { toast.error("Maxim 2 culori — scoate una întâi"); return; }
    else next = [...colors, code];
    save(next);
  };

  const dots = (
    <span className="inline-flex items-center gap-1 shrink-0" data-testid={`tool-dots-${projectId || regieId}`}>
      {colors.length > 0 && (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-full border text-[10px] font-bold whitespace-nowrap"
          style={{
            borderColor: TOOL_COLOR_META[colors[0]]?.hex,
            color: TOOL_COLOR_META[colors[0]]?.hex,
            background: `${TOOL_COLOR_META[colors[0]]?.hex}14`,
          }}
        >
          {colors.map((c) => (
            <span key={c} className="w-2.5 h-2.5 rounded-full border border-white/70 shadow-sm shrink-0" style={{ background: TOOL_COLOR_META[c]?.hex }} title={`Scule: ${TOOL_COLOR_META[c]?.label}`} />
          ))}
          scule {colors.map((c) => TOOL_COLOR_META[c]?.plural).join(" + ")}
        </span>
      )}
      {colors.length === 0 && isAdmin && (
        <span className="w-3 h-3 rounded-full border border-dashed border-zinc-400 opacity-50" title="Fără set de scule — click pentru a aloca" />
      )}
    </span>
  );

  if (!isAdmin) return colors.length ? dots : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center hover:scale-110 transition-transform cursor-pointer"
          title={colors.length ? `Scule: ${colors.map((c) => TOOL_COLOR_META[c]?.label).join(" + ")} · click pentru a schimba` : "Alocă un set de scule (click)"}
          data-testid={`tool-dots-btn-${projectId || regieId}`}
        >
          {dots}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] font-semibold text-zinc-500 mb-1.5">Seturi de scule (max 2) — se aplică pe toată durata</div>
        <div className="flex items-center gap-1.5">
          {Object.entries(TOOL_COLOR_META).map(([code, m]) => {
            const sel = colors.includes(code);
            return (
              <button
                key={code}
                type="button"
                disabled={saving}
                onClick={() => toggle(code)}
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-white text-[10px] font-bold transition-transform hover:scale-110 cursor-pointer disabled:opacity-50 ${sel ? "ring-2 ring-offset-1 ring-zinc-500" : ""}`}
                style={{ background: m.hex, borderColor: m.hex }}
                title={sel ? `Scoate ${m.label}` : `Adaugă ${m.label}`}
                data-testid={`tool-pick-${code}`}
              >
                {sel ? "✓" : ""}
              </button>
            );
          })}
          {colors.length > 0 && (
            <button
              type="button"
              disabled={saving}
              onClick={() => save([])}
              className="text-[10px] px-1.5 py-1 rounded border border-zinc-300 text-zinc-500 hover:bg-zinc-100 cursor-pointer disabled:opacity-50"
              title="Scoate toate culorile"
              data-testid="tool-pick-clear"
            >
              ✕ fără
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const toolColorsOfEntries = (entries = []) => {
  for (const e of entries) {
    if (e.allocation_colors && e.allocation_colors.length) return e.allocation_colors;
    if (e.allocation_color) return [e.allocation_color];
  }
  return [];
};

export const toolColorsBg = (colors = [], hexMap, alpha = "") => {
  if (!colors.length) return null;
  const h = (c) => `${hexMap[c]}${alpha}`;
  if (colors.length === 1) return h(colors[0]);
  return `linear-gradient(135deg, ${h(colors[0])} 0%, ${h(colors[0])} 50%, ${h(colors[1])} 50%, ${h(colors[1])} 100%)`;
};
